import MaskAndValid from "utils/maskAndValid";

export function compareConcorrenteAlfabetico(a, b) {
  const marcaA = String(a?.nome ?? a?.Nome ?? "").trim();
  const marcaB = String(b?.nome ?? b?.Nome ?? "").trim();
  const byMarca = marcaA.localeCompare(marcaB, "pt-BR", {
    sensitivity: "base",
  });
  if (byMarca !== 0) return byMarca;
  const prodA = String(
    a?.nomeProduto ?? a?.produtoReferencia ?? a?.ProdutoReferencia ?? "",
  ).trim();
  const prodB = String(
    b?.nomeProduto ?? b?.produtoReferencia ?? b?.ProdutoReferencia ?? "",
  ).trim();
  return prodA.localeCompare(prodB, "pt-BR", { sensitivity: "base" });
}

export function unwrapApiResult(data) {
  if (!data) return null;
  return data.Result !== undefined ? data.Result : data.result;
}

/** Documento da pessoa (CPF 11 ou CNPJ 14 dígitos) para rótulo no formulário. */
function formatCpfCnpjExibicao(raw) {
  if (raw == null || raw === "") return "";
  const digits = String(raw).replace(/\D/g, "");
  if (digits.length === 11) return MaskAndValid.maskCPF(digits);
  if (digits.length === 14) return MaskAndValid.maskCNPJ(digits);
  if (digits.length > 0) return digits;
  return String(raw).trim();
}

/**
 * Opção do autocomplete "Razão social": id estável `razaoSocial` (texto) e rótulo "nome | documento"
 * (formato esperado por `ignoreCnpjPunctuationInSearch` no Autocomplete).
 */
export function buildRazaoSocialOption(razaoSocial, cnpjRaw) {
  const razao = (razaoSocial ?? "").trim();
  const doc = formatCpfCnpjExibicao(cnpjRaw);
  return {
    razaoSocial: razao,
    razaoSocialLabel: doc ? `${razao} | ${doc}` : razao,
  };
}

/** Garante texto de grupo para UI e `grupoEconomico` vazio (envio como null na API). */
export function normalizeLojaForm(loja) {
  if (!loja) return null;
  const g = String(loja.grupoEconomico ?? "").trim();
  return {
    ...loja,
    grupoEconomico: g,
    grupoEconomicoExibicao: g || "—",
  };
}

function parseNumericId(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function mapLojaFromApi(d) {
  const id = d.id ?? d.Id;
  const razaoSocial = (d.razaoSocial ?? d.RazaoSocial ?? "").trim();
  const cnpjRaw = d.cnpj ?? d.Cnpj ?? d.CNPJ ?? "";
  return normalizeLojaForm({
    id: id != null ? String(id) : "",
    idPessoaLoja: d.idPessoaLoja ?? d.IdPessoaLoja,
    idGrupoEconomico: d.idGrupoEconomico ?? d.IdGrupoEconomico,
    razaoSocial,
    cnpj: String(cnpjRaw ?? "").trim(),
    grupoEconomico: (d.grupoEconomico ?? d.GrupoEconomico ?? "").trim(),
    cidade: d.cidade ?? d.Cidade ?? "",
    uf: d.uf ?? d.Uf ?? "",
  });
}

const OPCOES_CADASTRO_VAZIAS = {
  nomesComerciais: [],
  gramaturas: [],
  tecidoBases: [],
};

export function mapProdutosReferenciaCatalogoFromApi(data) {
  if (Array.isArray(data)) {
    const produtos = data.map(mapProdutoReferenciaFromApi);
    return {
      produtos,
      opcoesCadastro: {
        cama: buildOpcoesCadastroFromProdutosKarsten(produtos, "cama"),
        banho: buildOpcoesCadastroFromProdutosKarsten(produtos, "banho"),
      },
    };
  }

  const produtosRaw = data?.produtos ?? data?.Produtos ?? [];
  const opcoesRaw = data?.opcoesCadastro ?? data?.OpcoesCadastro ?? {};
  const produtos = Array.isArray(produtosRaw)
    ? produtosRaw.map(mapProdutoReferenciaFromApi)
    : [];

  const camaRaw = opcoesRaw.cama ?? opcoesRaw.Cama;
  const banhoRaw = opcoesRaw.banho ?? opcoesRaw.Banho;

  return {
    produtos,
    opcoesCadastro: {
      cama: camaRaw
        ? mapProdutoConcorrenteOpcoesFromApi(camaRaw)
        : buildOpcoesCadastroFromProdutosKarsten(produtos, "cama"),
      banho: banhoRaw
        ? mapProdutoConcorrenteOpcoesFromApi(banhoRaw)
        : buildOpcoesCadastroFromProdutosKarsten(produtos, "banho"),
    },
  };
}

export function mapProdutoReferenciaFromApi(d) {
  const id = parseNumericId(d.id ?? d.Id);
  const pk = d.precoKarsten ?? d.PrecoKarsten;
  const planejadosRaw =
    d.concorrentesPlanejados ?? d.ConcorrentesPlanejados ?? [];
  const concorrentesPlanejados = Array.isArray(planejadosRaw)
    ? [...planejadosRaw]
        .map((p) => {
          const nomeProduto =
            p.nomeProduto ??
            p.NomeProduto ??
            p.produtoReferencia ??
            p.ProdutoReferencia ??
            p.linha ??
            p.Linha ??
            "";
          return {
            idProdutoConcorrente: parseNumericId(
              p.idProdutoConcorrente ?? p.IdProdutoConcorrente,
            ),
            nome: p.nome ?? p.Nome ?? "",
            nomeProduto,
            produtoReferencia: nomeProduto || null,
            linha: nomeProduto,
            gramatura: p.gramatura ?? p.Gramatura ?? "",
            tecidoBase: p.tecidoBase ?? p.TecidoBase ?? "",
          };
        })
        .sort(compareConcorrenteAlfabetico)
    : [];
  return {
    id,
    sku: d.sku ?? d.Sku ?? "",
    nome: d.nome ?? d.Nome ?? "",
    linha: d.linha ?? d.Linha ?? "",
    categoria: d.categoria ?? d.Categoria ?? "",
    gramatura: d.gramatura ?? d.Gramatura ?? "",
    tecidoBase: d.tecidoBase ?? d.TecidoBase ?? "",
    listaSkuNome: d.listaSkuNome ?? d.ListaSkuNome ?? "",
    precoKarsten: pk != null ? Number(pk) : null,
    imagemUrl: d.imagemUrl ?? d.ImagemUrl ?? "",
    concorrentesPlanejados,
  };
}

/** Loja no POST — ids numéricos da estrutura comercial. */
export function mapLojaToApiPayload(loja) {
  if (!loja) return null;
  const grupo = loja.grupoEconomico;
  const grupoTrim =
    grupo == null || typeof grupo !== "string" ? "" : String(grupo).trim();
  return {
    id: loja.id ?? null,
    razaoSocial: loja.razaoSocial ?? null,
    grupoEconomico: grupoTrim === "" ? null : grupoTrim,
    cidade: loja.cidade ?? null,
    uf: loja.uf ?? null,
    idPessoaLoja: loja.idPessoaLoja ?? null,
    idGrupoEconomico: loja.idGrupoEconomico ?? null,
  };
}

/** Produto referência no POST — id numérico do catálogo. */
export function mapProdutoToApiPayload(produto) {
  if (!produto) return null;
  const id = parseNumericId(produto.id);
  return {
    id,
    sku: produto.sku ?? null,
    nome: produto.nome ?? null,
    linha: produto.linha ?? null,
    categoria: produto.categoria ?? null,
    gramatura: produto.gramatura ?? null,
    tecidoBase: produto.tecidoBase ?? null,
    imagemUrl: produto.imagemUrl ?? null,
  };
}

/** Concorrente no POST: id do catálogo + preços/foto. */
export function mapConcorrenteRowToApi(r) {
  const idProdutoConcorrente = parseNumericId(r.idProdutoConcorrente);
  if (!idProdutoConcorrente) return null;
  if (!r.encontrado) {
    return {
      idProdutoConcorrente,
      precoNormal: null,
      emPromocao: false,
      precoPromocional: null,
      fotoBase64: null,
    };
  }
  return {
    idProdutoConcorrente,
    precoNormal: r.precoNormal ?? null,
    emPromocao: !!r.emPromocao,
    precoPromocional: r.emPromocao ? (r.precoPromocional ?? null) : null,
    fotoBase64: r.fotoBase64 || null,
  };
}

export function initialConcorrenteRow(planned, { encontrado = true } = {}) {
  const p =
    typeof planned === "string"
      ? {
          nome: planned,
          nomeProduto: "",
          linha: "",
          gramatura: "",
          tecidoBase: "",
        }
      : (planned ?? {});
  const nomeProduto =
    p.nomeProduto ?? p.produtoReferencia ?? p.ProdutoReferencia ?? p.linha ?? p.Linha ?? "";
  return {
    idProdutoConcorrente: parseNumericId(p.idProdutoConcorrente),
    nome: p.nome ?? p.Nome ?? "",
    nomeProduto,
    produtoReferencia: nomeProduto,
    linha: nomeProduto,
    gramatura: p.gramatura ?? p.Gramatura ?? "",
    tecidoBase: p.tecidoBase ?? p.TecidoBase ?? "",
    encontrado,
    precoNormal: undefined,
    emPromocao: false,
    precoPromocional: undefined,
    fotoBase64: "",
  };
}

export function mapColetaHistoricoFromApi(d) {
  if (!d) return null;
  const lojaRaw = d.loja ?? d.Loja ?? {};
  const prodRaw = d.produto ?? d.Produto ?? {};
  const concRaw = d.concorrentes ?? d.Concorrentes ?? [];
  const idColeta = d.idColeta ?? d.IdColeta;
  const mapConc = (c) => ({
    idProdutoConcorrente: parseNumericId(
      c.idProdutoConcorrente ?? c.IdProdutoConcorrente,
    ),
    nome: c.nome ?? c.Nome ?? "",
    nomeProduto: c.nomeProduto ?? c.NomeProduto ?? "",
    precoNormal:
      c.precoNormal != null
        ? Number(c.precoNormal)
        : c.PrecoNormal != null
          ? Number(c.PrecoNormal)
          : null,
    emPromocao: !!(c.emPromocao ?? c.EmPromocao),
    precoPromocional:
      c.precoPromocional != null
        ? Number(c.precoPromocional)
        : c.PrecoPromocional != null
          ? Number(c.PrecoPromocional)
          : null,
    temFoto: !!(c.temFoto ?? c.TemFoto),
    encontrado: !!(c.encontrado ?? c.Encontrado),
  });
  return {
    idColeta,
    id: idColeta != null ? String(idColeta) : "",
    tipoColeta: d.tipoColeta ?? d.TipoColeta ?? "",
    dataColeta: d.dataColeta ?? d.DataColeta ?? "",
    representante: d.representante ?? d.Representante ?? "",
    dataEnvioUtc: d.dataEnvioUtc ?? d.DataEnvioUtc,
    loja: mapLojaFromApi(lojaRaw),
    produto: {
      id: parseNumericId(prodRaw.id ?? prodRaw.Id),
      sku: prodRaw.sku ?? prodRaw.Sku ?? "",
      nome: prodRaw.nome ?? prodRaw.Nome ?? "",
      linha: prodRaw.linha ?? prodRaw.Linha ?? "",
      categoria: prodRaw.categoria ?? prodRaw.Categoria ?? "",
      gramatura: prodRaw.gramatura ?? prodRaw.Gramatura ?? "",
      tecidoBase: prodRaw.tecidoBase ?? prodRaw.TecidoBase ?? "",
      imagemUrl: prodRaw.imagemUrl ?? prodRaw.ImagemUrl ?? "",
    },
    precoKarstenLoja: Number(d.precoKarstenLoja ?? d.PrecoKarstenLoja ?? 0),
    concorrentes: Array.isArray(concRaw) ? concRaw.map(mapConc) : [],
  };
}

export function mapColetasHistoricoPageFromApi(data) {
  if (!data) {
    return {
      items: [],
      totalCount: 0,
      page: 1,
      pageSize: 15,
      totalPages: 0,
    };
  }
  const itemsRaw = data.items ?? data.Items ?? [];
  const totalCount = Number(data.totalCount ?? data.TotalCount ?? 0);
  const page = Number(data.page ?? data.Page ?? 1);
  const pageSize = Number(data.pageSize ?? data.PageSize ?? 15);
  const totalPages = Number(data.totalPages ?? data.TotalPages ?? 0);
  return {
    items: Array.isArray(itemsRaw)
      ? itemsRaw.map(mapColetaHistoricoFromApi).filter(Boolean)
      : [],
    totalCount: Number.isNaN(totalCount) ? 0 : totalCount,
    page: Number.isNaN(page) || page < 1 ? 1 : page,
    pageSize: Number.isNaN(pageSize) || pageSize < 1 ? 15 : pageSize,
    totalPages: Number.isNaN(totalPages) ? 0 : totalPages,
  };
}

export function buildConcorrentesRowsFromProduto(full) {
  const planejados =
    full.concorrentesPlanejados ?? full.ConcorrentesPlanejados ?? [];
  if (Array.isArray(planejados) && planejados.length > 0) {
    return [...planejados]
      .sort(compareConcorrenteAlfabetico)
      .map((p) => initialConcorrenteRow(p));
  }
  return [];
}

/** Remove acentos para comparação de categoria (ex.: banhão → banhao). */
function normalizeCategoriaText(value) {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * Agrupa categoria do catálogo em <c>cama</c> ou <c>banho</c>.
 * Banhão → banho; Lençol / Jogo de cama → cama.
 */
export function normalizeCategoriaKey(value) {
  const cat = normalizeCategoriaText(value);
  if (!cat) return null;
  if (cat.includes("banho") || cat.includes("banhao")) return "banho";
  if (
    cat.includes("cama") ||
    cat.includes("lencol") ||
    cat.includes("jogo de cama")
  ) {
    return "cama";
  }
  return null;
}

export function produtoPermiteCadastroConcorrente(produto) {
  if (!produto?.id) return false;
  return normalizeCategoriaKey(produto.categoria) != null;
}

export function matchCategoriaProduto(categoriaProduto, categoriaSelecionada) {
  const prodCat = normalizeCategoriaKey(categoriaProduto);
  const sel = categoriaSelecionada === "banho" ? "banho" : "cama";
  if (prodCat) return prodCat === sel;
  return false;
}

function distinctSortedStrings(lists) {
  const set = new Set();
  (lists || []).forEach((list) => {
    (list || []).forEach((v) => {
      const t = String(v ?? "").trim();
      if (t) set.add(t);
    });
  });
  return [...set].sort((a, b) =>
    a.localeCompare(b, "pt-BR", { sensitivity: "base" }),
  );
}

/** Une listas de opções (API, catálogo local, produto em edição). */
export function mergeOpcoesCadastro(...fontes) {
  return {
    nomesComerciais: distinctSortedStrings(
      fontes.map((f) => f?.nomesComerciais),
    ),
    gramaturas: distinctSortedStrings(fontes.map((f) => f?.gramaturas)),
    tecidoBases: distinctSortedStrings(fontes.map((f) => f?.tecidoBases)),
  };
}

function getProdutosParaOpcoesCadastro(catalogo, categoria, produtoAtual) {
  const sel = categoria === "banho" ? "banho" : "cama";
  const lista = Array.isArray(catalogo) ? catalogo : [];
  let filtered = lista.filter((p) => matchCategoriaProduto(p.categoria, sel));

  if (produtoAtual?.id != null) {
    const jaInclui = filtered.some((p) => p.id === produtoAtual.id);
    const catProd = normalizeCategoriaKey(produtoAtual.categoria);
    if (!jaInclui && catProd === sel) {
      filtered = [...filtered, produtoAtual];
    }
  }

  return filtered;
}

/** Opções de cadastro conforme produtos Karsten ativos da categoria. */
export function buildOpcoesCadastroFromProdutosKarsten(
  produtosCatalogo,
  categoria,
  produtoAtual = null,
) {
  const filtered = getProdutosParaOpcoesCadastro(
    produtosCatalogo,
    categoria,
    produtoAtual,
  );
  if (categoria === "banho") {
    return {
      nomesComerciais: [],
      gramaturas: distinctSortedStrings([filtered.map((p) => p.gramatura)]),
      tecidoBases: [],
    };
  }
  return {
    nomesComerciais: [],
    gramaturas: [],
    tecidoBases: distinctSortedStrings([filtered.map((p) => p.tecidoBase)]),
  };
}

export function mapConcorrenteMarcaFromApi(d) {
  const id = parseNumericId(d.idConcorrenteCatalog ?? d.IdConcorrenteCatalog);
  return {
    idConcorrenteCatalog: id,
    nome: (d.nome ?? d.Nome ?? "").trim(),
  };
}

export const MARCA_GENERICA_NOME = "Genérica";

export function isMarcaGenericaNome(nome) {
  return normalizeCategoriaText(nome) === "generica";
}

export function resolveMarcaGenericaId(marcas) {
  const found = (marcas || []).find((m) => isMarcaGenericaNome(m.nome));
  return found?.idConcorrenteCatalog ?? null;
}

export function mapMarcasConcorrentesFromApi(data) {
  if (!Array.isArray(data)) return [];
  return data
    .map(mapConcorrenteMarcaFromApi)
    .filter((m) => m.idConcorrenteCatalog != null && m.nome.length > 0)
    .sort((a, b) =>
      a.nome.localeCompare(b.nome, "pt-BR", { sensitivity: "base" }),
    );
}

export function mapProdutoConcorrenteOpcoesFromApi(data) {
  if (!data) {
    return { ...OPCOES_CADASTRO_VAZIAS };
  }
  const pickStrings = (arr) =>
    Array.isArray(arr)
      ? arr
          .map((x) => String(x ?? "").trim())
          .filter((x) => x.length > 0)
      : [];
  return {
    nomesComerciais: pickStrings(data.nomesComerciais ?? data.NomesComerciais),
    gramaturas: pickStrings(data.gramaturas ?? data.Gramaturas),
    tecidoBases: pickStrings(data.tecidoBases ?? data.TecidoBases),
  };
}

export function mapProdutoConcorrenteCadastroFromApi(data) {
  const concRaw = data?.concorrente ?? data?.Concorrente ?? {};
  const nomeProduto =
    concRaw.nomeProduto ??
    concRaw.NomeProduto ??
    concRaw.produtoReferencia ??
    concRaw.ProdutoReferencia ??
    concRaw.linha ??
    concRaw.Linha ??
    "";
  return {
    mensagem: data?.mensagem ?? data?.Mensagem ?? "Produto concorrente cadastrado.",
    concorrente: {
      idProdutoConcorrente: parseNumericId(
        concRaw.idProdutoConcorrente ?? concRaw.IdProdutoConcorrente,
      ),
      nome: concRaw.nome ?? concRaw.Nome ?? "",
      nomeProduto,
      produtoReferencia: nomeProduto || null,
      linha: nomeProduto,
      gramatura: concRaw.gramatura ?? concRaw.Gramatura ?? "",
      tecidoBase: concRaw.tecidoBase ?? concRaw.TecidoBase ?? "",
    },
  };
}

export function formatConcorrenteRowLabel(row) {
  if (!row) return "";
  const linhaProduto = String(
    row.nomeProduto || row.produtoReferencia || row.linha || "",
  ).trim();
  const parts = [row.nome, linhaProduto]
    .filter(Boolean)
    .map((s) => String(s).trim())
    .filter((s) => s.length > 0);
  const meta = [row.gramatura, row.tecidoBase]
    .filter(Boolean)
    .map((s) => String(s).trim())
    .filter((s) => s.length > 0);
  const main = parts.join(" — ");
  if (!meta.length) return main;
  return `${main} (${meta.join(" · ")})`;
}
